
import { json, redirect } from "@remix-run/node";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import {
  loginWithEmailPassword,
  isAuthenticatedRedirect,
} from "~/services/auth.server";
import { Form, useActionData, useLocation } from "@remix-run/react";
import { defaultMetaTagsBuilder } from "~/utils/meta";
import { useEffect } from "react";
import { putToast } from "~/services/toast.server";
import {LoginForm} from "~/components/login-form";

export const meta: MetaFunction = ({ location }) => {
  const url = new URL(location.pathname, "https://app.7qz.io");
  return defaultMetaTagsBuilder({
    title: "Login",
    description: "Login to your 7qz account",
    url: url,
  });
};

export async function loader({ request }: LoaderFunctionArgs) {
  await isAuthenticatedRedirect(request);

  return null;
}

export default function Login() {
  const location = useLocation();
  const actionData = useActionData<typeof action>();
  const clearStorageQuery = new URLSearchParams(location.search).get(
    "clearStorage"
  );

  useEffect(() => {
    if (clearStorageQuery === "true") {
      localStorage.clear();
    }
  }, [clearStorageQuery]);

  return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6 md:p-10">
        <div >
          <Form method="post">
          <LoginForm email={actionData?.values?.email ?? ''} password={actionData?.values?.password ?? ''} error={actionData?.errorMessage}/>
          </Form>
        </div>
      </div>
  );
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get("email") as string;
  const password = formData.get("password") as string;

  const loginResult = await loginWithEmailPassword({
    email,
    password,
    headers: request.headers,
  });

  if (!loginResult.success || !loginResult.payload) {
    return json({
      errorMessage: loginResult?.error?.message ?? "An error has occurred",
      values: {
        email: email,
        password: password,
        
      },
    });
  }

  const authHeaders = loginResult.payload.headers;

  // at this point the user is authenticated and we have the cookie set in the headers
  // add a toast message to the session
  const toastHeaders = await putToast({
    type: "success",
    message: "You have successfully logged in",
  });

  // check if the request url had a redirect query param
  const redirectUrl = new URL(request.url).searchParams.get("redirect");
  const sendUrl = redirectUrl ? redirectUrl : "/";

  return redirect(sendUrl, {
    headers: [
      ["Set-Cookie", authHeaders.get("Set-Cookie") ?? ""],
      ["Set-Cookie", toastHeaders.get("Set-Cookie") ?? ""],
    ],
  });
}
